/** @jsxImportSource preact */
import { RequestEntry } from "./contact/support-request-receipt";
import { StatusCard } from "./cards/status-card";
import { SupportRequestContext } from "./contact/support-request-card";
import type {
  CustomerAuth,
  VerifyEmailAction,
} from "../protocol/customer-auth";
import type { OrderActionFailure } from "../conversation/order-failure";
// 这里的 `m` 已经是那条消息的 props，文案命名空间只能叫别的，否则会被静默遮蔽。
import { Fragment, type ComponentChildren } from "preact";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";
import {
  BusinessOrderCard,
  PendingBusinessCards,
} from "./order-card/business-order-card";
import * as copy from "../paraglide/messages.js";
import type { CartOpeners } from "./cart/cart-controller";
import { CompareCard } from "./compare-card";
import { ProductCard } from "./product-card";
import { AlertIcon } from "./icons";
import {
  WidgetCharacter,
  type WidgetCharacterMotion,
  type WidgetCharacterVariant,
} from "./widget-character";
import type { ResolvedMessage } from "./message";
import { MessageImage } from "./message-image";
import { MessageMarkdown } from "./message-markdown";
import {
  Bubble,
  BubbleContent,
  Marker,
  MarkerContent,
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
} from "./message-parts";
import { SizeCard } from "./size-card";

export interface WidgetMessageProps {
  m: ResolvedMessage;
  /** Inbox 回放的只读转人工标记，不提供动作。 */
  handedOff?: boolean;
  formatCardDateTime?: (iso: string) => string;
  /** 商品卡上的「+」靠它打开加购面板；没有就不摆加购。 */
  cart?: CartOpeners;
  /** 仅会话最后一条 AI 消息传入动作；历史和后台回放只显示静态轮廓。 */
  characterMotion?: Extract<
    WidgetCharacterMotion,
    "quiet" | "looking" | "waiting" | "ack" | "blocked" | "dozing"
  >;
  /** 连续 AI 回复只在第一条画角色，后面几条留出同宽的空位对齐正文。 */
  showAvatar?: boolean;
  /** 同一发言人连续消息只有组首第一块气泡缩角，与头像是否显示分开控制。 */
  groupStart?: boolean;
  /** 打开聊天窗那一刻最新回复的头像：入口角色原地淡出时它同时淡入，换页醒来也是它先睁眼。 */
  landing?: boolean;
  /** 面板已经判断过主色压不压得住白眼睛，这里照着画。 */
  characterVariant?: WidgetCharacterVariant;
  /** 未送达那条上的重试：把这条消息的原文和它自己的 id 一起传回去。 */
  onRetry?: (text: string, msgId: string) => void;
  onOrderAction?: (
    messageId: string,
    stateIndex: number,
    action: "select" | "more" | "not_received" | "check",
    orderRef?: string,
  ) => void;
  orderBusy?: boolean;
  orderFailures?: Record<string, OrderActionFailure>;
  onVerifyTask?: (messageId: string, email?: string) => void;
  verificationBusy?: boolean;
  /** Playground 没有店铺登录:登录卡改成填店内顾客的邮箱。 */
  playgroundVerification?: boolean;
  verificationAuth?: CustomerAuth;
  onVerifyEmail?: (
    messageId: string,
    input: Parameters<VerifyEmailAction>[0],
  ) => ReturnType<VerifyEmailAction>;
  /** 后台回放的链接开新标签页。 */
  linksInNewTab?: boolean;
  /** 面板打开之后才到的 AI 回复：正文逐字流出，图片、卡片等正文出到它们那儿再出来。 */
  reveal?: boolean;
  /** 正文和业务卡之后的消息动作。 */
  actions?: ComponentChildren;
  feedbackEdge?: "start" | "end";
}

/** 设计稿 11a：回复流完 120ms 后商品卡浮起，多张从右往左每张错开 40ms。 */
const CARDS_DELAY_MS = 120;
const CARD_STEP_MS = 40;
/** 骨架流光一个来回的时长，和设计稿 12a 的 shimmer 一致；骨架只摆到这一行浮起为止。 */
const SKELETON_SHIMMER_MS = 1400;
/** 每字 2ms，整条 0.3–1 秒：回复已经让买家等过一轮，放得再慢就是第二次等待。 */
const REVEAL_MS_PER_CHAR = 2;
const REVEAL_MIN_MS = 300;
const REVEAL_MAX_MS = 1000;

/** 回复流完后卡片浮起；`order` 是这张卡在错开序列里的位置。 */
function cardEntrance(order: number): string {
  return `gnReplyCardIn .36s cubic-bezier(.2,.8,.2,1) ${String(CARDS_DELAY_MS + order * CARD_STEP_MS)}ms both`;
}

/**
 * 返回此刻露出的正文字数，以及流完之前要占住的整条高度。时钟从第一帧起算：回复到时标签页在后台，买家切回来才开始放。
 * 减少动态效果时不流，直接给全文。
 */
function useReveal(reveal: boolean, length: number) {
  const [streaming] = useState(
    () =>
      reveal && !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const box = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const [measured, setMeasured] = useState(!streaming);
  // 首次渲染用全文排版，量完才退回第一个字，这一帧不会画出来。
  const [shown, setShown] = useState(length);
  // 逐字露出时会话总高度每帧都变，WebKit 会在重排里把锚在顶部的滚动位置夹下去，
  // 滚动原语再拉回来，iPhone 上整段会话跟着狂抖；先按全文量出高度占住，流的过程中总高度不变。
  useLayoutEffect(() => {
    if (!streaming) return;
    const full = box.current?.getBoundingClientRect().height ?? 0;
    if (full > 0) setHeight(full);
    setShown(Math.min(1, length));
    setMeasured(true);
  }, [streaming, length]);
  useEffect(() => {
    if (!streaming) return;
    const duration = Math.min(
      REVEAL_MAX_MS,
      Math.max(REVEAL_MIN_MS, length * REVEAL_MS_PER_CHAR),
    );
    let start: number | undefined;
    let frame = requestAnimationFrame(function tick(now) {
      start ??= now;
      const next = Math.min(
        length,
        Math.max(1, Math.ceil(((now - start) / duration) * length)),
      );
      setShown(next);
      if (next < length) frame = requestAnimationFrame(tick);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [streaming, length]);
  return {
    box,
    streaming,
    shown,
    measuring: !measured,
    height: shown < length ? height : undefined,
  };
}

/**
 * 一条消息：AI 角色在左，气泡与附件依次排列在内容列。
 * 组件只按 `m` 里算好的 token 画，不自己推导任何颜色。
 */
export function WidgetMessage({
  m,
  handedOff = false,
  formatCardDateTime,
  onRetry,
  onOrderAction,
  orderBusy,
  orderFailures,
  onVerifyTask,
  verificationBusy,
  playgroundVerification,
  verificationAuth,
  onVerifyEmail,
  cart,
  characterMotion,
  showAvatar = true,
  groupStart = true,
  landing = false,
  characterVariant = "solid",
  linksInNewTab = false,
  reveal = false,
  actions,
  feedbackEdge = "start",
}: WidgetMessageProps) {
  const supportActions = useContext(SupportRequestContext);
  const { message: msg, tokens: t } = m;
  // 正常显示的订单卡和验证卡自带联系商家的入口；同一条消息里不再给请求另放一个。
  // 授权变化后订单卡会变成不可用说明，那时没有入口，请求得自己留一个。
  const requestEntry = !msg.blocks?.some(
    (block) =>
      (block.type === "order_detail" &&
        block.status === "ok" &&
        !!onOrderAction) ||
      ((block.type === "verification" ||
        block.status === "needs_verification") &&
        !(
          block.type === "verification" &&
          block.status === "ok" &&
          block.data.status === "verified"
        )),
  );
  let textLength = 0;
  const blockStarts = m.blocks.map((block) => {
    const start = textLength;
    if (block.type === "text") textLength += block.text.length;
    return start;
  });
  const { box, streaming, shown, measuring, height } = useReveal(
    reveal,
    textLength,
  );
  // 量高度那一帧只有正文按全文排，其余照流式处理：操作行、商品卡量完再藏会让整行变矮，照样把滚动位置夹下去。
  const revealing = measuring || shown < textLength;
  // 与 MESSAGE_STYLES 里 --gn-avatar-size 的两档一致。
  const avatarSize = t.editorial ? 20 : 24;
  // 历史消息旁：实色角色只剩无眼睛的轮廓；水彩角色去掉眼睛像个失明的分身，改成闭眼、半透明。
  const asleep =
    characterMotion === undefined && characterVariant === "watercolor";
  // 设计稿 11a：正文流出时角色睁圆眼睛说话；流完那一眨眼在 animations.ts 里按 data-spoke 播。
  const motion =
    characterMotion === "quiet" && revealing ? "speaking" : characterMotion;

  return (
    <Message
      elementRef={box}
      align={m.align === "flex-end" ? "end" : "start"}
      data-message-role={msg.role}
      data-editorial={t.editorial || undefined}
      data-group-start={groupStart || undefined}
      data-revealing={revealing || undefined}
      // 流式回复顶替的是「生成中」那行，整行再上滑一次，角色会跳一下；入场交给正文自己流出来。
      data-streaming={streaming || undefined}
      data-spoke={(streaming && !revealing) || undefined}
      data-typing={msg.typing || undefined}
      data-wake={m.wake || undefined}
      data-feedback-edge={actions ? feedbackEdge : undefined}
      // 流到一半的正文每帧都在换，不让读屏逐帧播报，也不让键盘停在半截链接上；流完一次性读整段。
      inert={revealing || undefined}
      style={{
        minHeight: height,
        // 入场动画写在店面样式里（头像和正文分开播），这里只给时刻；消息里自己带入场动画的部分（求助卡）从同一时刻起算。
        "--gn-enter-delay": m.delay,
        "--gn-skeleton-iterations": m.wake
          ? String(Number.parseFloat(m.delay) / SKELETON_SHIMMER_MS)
          : undefined,
        "--gn-message-bg": m.background,
        "--gn-message-fg": m.color,
        "--gn-message-radius": m.radius,
        "--gn-message-max-width": m.maxWidth,
        "--gn-message-padding": m.padding,
        "--gn-message-leading": m.lineHeight,
        "--gn-message-line": t.line,
      }}
    >
      {msg.role === "ai" &&
        (showAvatar ? (
          <MessageAvatar
            data-active={characterMotion !== undefined || undefined}
            data-landing={landing || undefined}
          >
            <MarkerContent>
              <WidgetCharacter
                size={avatarSize}
                color={t.accentFill}
                eyeColor={t.accentFillFg}
                radius={`min(${String(Math.floor(avatarSize / 3))}px, ${t.bubbleRadius})`}
                motion={motion ?? (asleep ? "asleep" : "still")}
                showEyes={characterMotion !== undefined || asleep}
                variant={characterVariant}
              />
            </MarkerContent>
          </MessageAvatar>
        ) : (
          <MessageAvatar />
        ))}
      <MessageContent>
        {msg.typing ? (
          <GeneratingNote progress={msg.text || undefined} />
        ) : msg.alert ? (
          <Marker style={{ minHeight: "var(--gn-avatar-size)" }}>
            <MarkerContent>
              <span
                role="alert"
                style={{
                  color: t.dangerText,
                  display: "flex",
                  fontSize: 12,
                  gap: 4,
                }}
              >
                {/* 图标在一行高的盒子里居中：跟着文字排会贴基线偏高，整块居中又会在折行时落到两行中间。 */}
                <span
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flex: "none",
                    height: "1lh",
                  }}
                >
                  <AlertIcon size={14} />
                </span>
                {msg.text}
              </span>
            </MarkerContent>
          </Marker>
        ) : (
          m.blocks.map((block, index) => {
            const start = blockStarts[index] ?? 0;
            if (block.type === "text" ? shown <= start : shown < start)
              return null;
            if (block.type === "transfer") return null;
            if (block.type === "image")
              return (
                <MessageImage
                  key={`${block.id}:${block.url}`}
                  url={block.url}
                  alt={block.alt}
                  tokens={t}
                />
              );
            if (block.type === "text" && !block.text) return null;
            return (
              <Bubble
                key={block.id}
                variant={msg.role === "user" ? "default" : "secondary"}
              >
                <BubbleContent>
                  {block.type === "unknown" ? (
                    copy.widget_message_unsupported()
                  ) : msg.role === "ai" && msg.textFormat !== "plain" ? (
                    <MessageMarkdown
                      text={block.text}
                      linksInNewTab={linksInNewTab}
                      reveal={
                        shown - start < block.text.length
                          ? (shown - start) / block.text.length
                          : undefined
                      }
                    />
                  ) : (
                    block.text
                  )}
                </BubbleContent>
              </Bubble>
            );
          })
        )}

        {/* 未送达：气泡压淡之外还得写明白，光靠透明度买家读不出是「没发出去」。
          重试的字不压淡——那是这会儿唯一能点的东西。 */}
        {msg.failed === true && (
          <MessageFooter style={{ color: t.dangerText }}>
            <AlertIcon size={11} />
            <span>{copy.widget_message_undelivered()}</span>
            {onRetry !== undefined && (
              <button
                onClick={() => {
                  onRetry(msg.text ?? "", msg.id);
                }}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "inherit",
                  cursor: "pointer",
                  font: "inherit",
                  fontWeight: 600,
                  // 手机上这是买家唯一能点的补救入口。字不能再大了，就把可点范围
                  // 撑到 24px（WCAG 2.5.8），负 margin 把撑出来的部分收回去，
                  // meta 行的排布一个像素都不动。
                  margin: "-5px -7px",
                  padding: "5px 7px",
                  textDecoration: "underline",
                }}
                type="button"
              >
                {copy.widget_message_retry()}
              </button>
            )}
          </MessageFooter>
        )}

        {/* 尺码卡、对比卡紧跟正文：正文说理由，卡上摆数据；商品卡是之后的推荐。 */}
        {!revealing && msg.sizeCard !== undefined && (
          <SizeCard
            animation={streaming ? cardEntrance(0) : undefined}
            card={msg.sizeCard}
            tokens={t}
          />
        )}

        {!revealing && m.compareCard !== undefined && (
          <CompareCard
            animation={streaming ? cardEntrance(0) : undefined}
            card={m.compareCard}
            linksInNewTab={linksInNewTab}
            tokens={t}
          />
        )}

        {!revealing && m.products.length > 0 && (
          <div
            class="gn-product-list"
            style={{
              boxSizing: "border-box",
              display: "flex",
              gap: 8,
              // 横向滚动会把商品卡的柔阴影裁掉；四周留出阴影的位置，再用负边距抵回，排版不变。
              margin: "-8px -8px -28px",
              minWidth: 0,
              overflowX: "auto",
              padding: "8px 8px 28px",
              scrollbarWidth: "none",
              width: "calc(100% + 16px)",
            }}
          >
            {m.products.map((product, index) => (
              <ProductCard
                key={`${product.itemId ?? product.title}:${product.url ?? ""}`}
                animation={
                  streaming
                    ? cardEntrance(m.products.length - 1 - index)
                    : undefined
                }
                linksInNewTab={linksInNewTab}
                product={product}
                tokens={t}
                cart={cart}
              />
            ))}
          </div>
        )}

        {!revealing &&
          !!msg.replyStates?.length &&
          !msg.blocks &&
          (msg.blocksFailed ? (
            <StatusCard
              tokens={t}
              icon="search"
              title={copy.widget_blocks_pending()}
            />
          ) : (
            <PendingBusinessCards
              replyStates={msg.replyStates ?? []}
              tokens={t}
            />
          ))}
        {!revealing && msg.blocksFailed && !!msg.blocks?.length && (
          <div role="status">{copy.widget_blocks_refresh_failed()}</div>
        )}
        {!revealing &&
          msg.blocks?.map((block) => (
            <Fragment key={`${block.type}:${String(block.state_index)}`}>
              <BusinessOrderCard
                block={block}
                supportCard={
                  block.type === "support_request" && block.status === "ok"
                    ? msg.supportRequests?.find(
                        (card) =>
                          card.payload.request_id === block.data.request_id,
                      )
                    : undefined
                }
                onContactStore={
                  supportActions?.open &&
                  (block.type !== "support_request" || requestEntry)
                    ? (ref, orderReference) => {
                        void supportActions.open?.(msg.id, ref, orderReference);
                      }
                    : undefined
                }
                tokens={t}
                loading={orderBusy}
                disabled={
                  (block.type !== "order_list" &&
                    (msg.replyStates?.[block.state_index]?.task_version ??
                      msg.maxTaskVersion ??
                      0) < (msg.maxTaskVersion ?? 0)) ||
                  msg.disabledBlocks?.includes(
                    `${msg.id}:${String(block.state_index)}`,
                  )
                }
                onAction={
                  onOrderAction
                    ? (action, ref) => {
                        onOrderAction(msg.id, block.state_index, action, ref);
                      }
                    : undefined
                }
                verificationBusy={verificationBusy}
                playgroundVerification={playgroundVerification}
                verificationAuth={verificationAuth}
                onVerifyEmail={
                  onVerifyEmail
                    ? (input) => onVerifyEmail(msg.id, input)
                    : undefined
                }
                onVerify={
                  onVerifyTask
                    ? (email) => {
                        onVerifyTask(msg.id, email);
                      }
                    : undefined
                }
                formatDateTime={formatCardDateTime}
              />
              {orderFailures?.[`${msg.id}:${String(block.state_index)}`] && (
                <StatusCard
                  tokens={t}
                  icon="lock"
                  title={copy.widget_order_error_upstream()}
                />
              )}
            </Fragment>
          ))}
        {!revealing &&
          requestEntry &&
          msg.supportOpening &&
          !msg.supportRequests?.length && (
            <RequestEntry
              tokens={t}
              onOpen={() => {
                void supportActions?.open?.(
                  msg.id,
                  msg.supportOpening?.ref,
                  msg.supportOpening?.orderReference,
                );
              }}
            />
          )}
        {!revealing &&
          msg.replayBlocks?.map((block, index) => (
            <BusinessOrderCard
              key={index}
              block={block}
              readOnly
              formatDateTime={formatCardDateTime}
              tokens={t}
            />
          ))}

        {!revealing && handedOff && (
          <Marker style={{ color: t.faint }}>
            <MarkerContent>{copy.widget_replay_handed_off()}</MarkerContent>
          </Marker>
        )}

        {!revealing && actions && (
          <div
            class="gn-reply-tail"
            style={{ maxWidth: "var(--gn-message-max-width)" }}
          >
            {actions}
          </div>
        )}

        {/* 来源行暂时不画：后端说 grounding.label 目前不准（GON-115）。
          msg.source 照常收着、存进快照，后端修好后把这一行加回来即可。 */}
      </MessageContent>
    </Message>
  );
}

const GENERATING_PHRASES = [
  copy.widget_generating_racks,
  copy.widget_generating_deals,
  copy.widget_generating_catalog,
  copy.widget_generating_shelves,
  copy.widget_generating_stockroom,
  copy.widget_generating_details,
];
const PHRASE_HOLD_MS = 2200;
const PHRASE_FADE_MS = 120;
/** 设计稿写「超过 8s 固定」，但每句至少停 2.2s，所以第四句停满后（8.8s）才换。 */
const RANDOM_PHRASES = 3;

function GeneratingNote({ progress }: { progress: string | undefined }) {
  return (
    <Marker
      class="gn-generating"
      role="status"
      aria-label={copy.widget_message_replying()}
    >
      <GeneratingText progress={progress} />
    </Marker>
  );
}

/** 生成中那句扫光字：后端有进度就只显示进度，没有就轮换购物短语。 */
export function GeneratingText({ progress }: { progress: string | undefined }) {
  const [phrases] = useState(() => {
    const pool = [...GENERATING_PHRASES];
    return [
      copy.widget_composer_busy,
      ...Array.from({ length: RANDOM_PHRASES }, () =>
        pool.splice(Math.floor(Math.random() * pool.length), 1),
      ).flat(),
    ];
  });
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    let current = 0;
    let fade: ReturnType<typeof setTimeout> | undefined;
    const timer = setInterval(() => {
      if (current === RANDOM_PHRASES) clearInterval(timer);
      setLeaving(true);
      fade = setTimeout(() => {
        current += 1;
        setStep(current);
        setLeaving(false);
      }, PHRASE_FADE_MS);
    }, PHRASE_HOLD_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(fade);
    };
  }, []);
  const text = progress ?? (phrases[step] ?? copy.widget_generating_still)();

  // 词库句子只是陪伴，忙碌由输入区或收起入口另行播报，不再每 2.2s 念一遍。
  return (
    <MarkerContent
      key={text}
      class="shimmer"
      aria-hidden={progress === undefined || undefined}
      data-leaving={(progress === undefined && leaving) || undefined}
    >
      {text}
    </MarkerContent>
  );
}
